import { useLayoutEffect, useRef, type KeyboardEvent } from "react";
import { InputBase, type SxProps, type Theme } from "@mui/material";
import { caretAfterFormat, formatGrouped } from "../pipelineAmounts";

type GroupedNumberInputProps = {
  /** Formatted text, e.g. "28,463,000". */
  value: string;
  onChange: (formatted: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  ariaLabel: string;
  sx?: SxProps<Theme>;
};

/** Amount input that adds thousands separators as you type. */
export function GroupedNumberInput({
  value,
  onChange,
  onKeyDown,
  disabled,
  autoFocus,
  ariaLabel,
  sx,
}: GroupedNumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number | null>(null);

  // Put the caret back after React re-renders the re-formatted value.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (
      input &&
      caretRef.current !== null &&
      document.activeElement === input
    ) {
      input.setSelectionRange(caretRef.current, caretRef.current);
    }
    caretRef.current = null;
  }, [value]);

  return (
    <InputBase
      inputRef={inputRef}
      value={value}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={(e) => {
        const typed = e.target.value;
        const formatted = formatGrouped(typed);
        caretRef.current = caretAfterFormat(
          typed,
          e.target.selectionStart ?? typed.length,
          formatted,
        );
        onChange(formatted);
      }}
      onKeyDown={onKeyDown}
      inputProps={{ "aria-label": ariaLabel, inputMode: "decimal" }}
      sx={sx}
    />
  );
}
